import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertTriangle, Trash2, Eye, RefreshCw, FolderKanban, Receipt } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function OrphanedProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("projects"); // "projects" or "services"
  const [deletingAll, setDeletingAll] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const fetchOrphanedData = async () => {
    setLoading(true);
    try {
      const [projectsRes, servicesRes] = await Promise.all([
        api.get("/unified-projects?include_orphaned=true"),
        api.get("/debug/unassigned-services?year=2026")  // Use debug endpoint that matches frontend logic
      ]);
      setProjects(projectsRes.data);
      setServices(servicesRes.data.services || []);
    } catch (error) {
      console.error("Failed to fetch orphaned data:", error);
      toast.error("Failed to load orphaned data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrphanedData();
  }, []);

  const handleDeleteProject = async (projectId) => {
    try {
      await api.delete(`/unified-projects/${projectId}`);
      toast.success("Project deleted");
      setProjects(projects.filter(p => p.id !== projectId));
      setSelectedProject(null);
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleDeleteAllOrphaned = async () => {
    setDeletingAll(true);
    try {
      if (deleteType === "projects") {
        const response = await api.delete("/unified-projects-orphaned/bulk-delete");
        toast.success(`Deleted ${response.data.deleted_count} orphaned projects`);
        setProjects([]);
      } else if (deleteType === "services") {
        const response = await api.delete("/debug/unassigned-services/bulk-delete?year=2026");
        toast.success(`Deleted ${response.data.deleted_count} unassigned services`);
        setServices([]);
      }
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(`Failed to delete orphaned ${deleteType}`);
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteV2Projects = async () => {
    try {
      const response = await api.delete("/sales-projects-v2-orphaned/bulk-delete");
      toast.success(`Deleted ${response.data.deleted_count} orphaned v2 projects`);
      fetchOrphanedData();
    } catch (error) {
      toast.error("Failed to delete orphaned v2 projects");
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    if (!amount) return "—";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="p-6 space-y-6" data-testid="orphaned-projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
            Orphaned Data Cleanup
          </h1>
          <p className="text-zinc-400 mt-1">
            Projects and services with no manager or inactive manager assignment
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchOrphanedData}
            disabled={loading}
            data-testid="refresh-orphaned-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleDeleteV2Projects}
            className="text-orange-400 border-orange-400/50 hover:bg-orange-900/20"
          >
            Clean V2 Projects
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
        </div>
      ) : (
        <Tabs defaultValue="projects" className="space-y-4">
          <TabsList className="bg-zinc-800">
            <TabsTrigger value="projects" className="data-[state=active]:bg-zinc-700">
              <FolderKanban className="h-4 w-4 mr-2" />
              Projects ({projects.length})
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-zinc-700">
              <Receipt className="h-4 w-4 mr-2" />
              Services ({services.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {projects.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <div className="text-zinc-400">
                    <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No orphaned projects found</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-yellow-500">
                      {projects.length} Orphaned Project{projects.length !== 1 ? "s" : ""}
                    </CardTitle>
                    <CardDescription>Projects affecting statistics</CardDescription>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => { setDeleteType("projects"); setDeleteDialogOpen(true); }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-zinc-50">{project.name}</span>
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500/50">
                              {project.sales_manager_name || "No Manager"}
                            </Badge>
                          </div>
                          <div className="text-sm text-zinc-400 mt-1">
                            Client: {project.client_name || "Unknown"}
                            {project.budget_usd > 0 && ` • Budget: ${formatCurrency(project.budget_usd)}`}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => setSelectedProject(project)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="services">
            {services.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="py-12 text-center">
                  <div className="text-zinc-400">
                    <Eye className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No orphaned services found</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-yellow-500">
                      {services.length} Unassigned Service{services.length !== 1 ? "s" : ""}
                    </CardTitle>
                    <CardDescription>Services that appear in "Unassigned" row in Sales P&L</CardDescription>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => { setDeleteType("services"); setDeleteDialogOpen(true); }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {services.map((service, idx) => (
                      <div
                        key={service.id || idx}
                        className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-zinc-700"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="font-medium text-zinc-50">{service.name}</span>
                            <Badge variant="secondary">{formatCurrency(service.amount, service.currency)}</Badge>
                            {service.is_paid && <Badge className="bg-green-600">Paid</Badge>}
                            <Badge variant="outline" className="text-yellow-500 border-yellow-500/50">
                              {service.reason === "no_manager" ? "No Manager" : "Inactive Manager"}
                            </Badge>
                          </div>
                          <div className="text-sm text-zinc-400 mt-1">
                            Project: {service.project_name || "Unknown"} • Month: {service.month} • Prob: {service.probability || 0}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Delete Single Project Dialog */}
      <AlertDialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Delete Project?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete "{selectedProject?.name}" and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-zinc-50 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteProject(selectedProject?.id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete All Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete All Orphaned {deleteType === "projects" ? "Projects" : "Services"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete {deleteType === "projects" ? projects.length : services.length} orphaned {deleteType}.
              <br /><br />
              <strong className="text-yellow-500">This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-zinc-50 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteAllOrphaned} disabled={deletingAll}>
              {deletingAll ? "Deleting..." : "Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
